import React, { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { LoadingSpinner } from './LoadingSpinner';

interface StandaloneAppRedirectProps {
  appName: string;
}

/**
 * Component that handles redirection for standalone app routes
 * Redirects users to their primary organization or organization selection
 */
const StandaloneAppRedirect: React.FC<StandaloneAppRedirectProps> = ({ appName }) => {
  const { user, loading: authLoading } = useAuth();
  const { currentOrganization, userOrganizations, isLoading: orgLoading } = useOrg();
  const navigate = useNavigate();

  useEffect(() => {
    // Don't redirect if still loading authentication or organization data
    if (authLoading || orgLoading) {
      return;
    }

    // If not authenticated, redirect to login
    if (!user) {
      navigate('/');
      return;
    }

    // If user has a current organization or primary organization, redirect there
    if (currentOrganization) {
      console.log(`🔄 Redirecting standalone /${appName} to organization-specific route`);
      navigate(`/o/${currentOrganization.id}/${appName}`);
      return;
    }

    // If user has organizations but no current one selected, find primary
    if (userOrganizations && userOrganizations.length > 0) {
      // For now, use the first organization as primary
      // TODO: Implement proper primary organization logic
      const primaryOrg = userOrganizations[0];
      console.log(`🔄 Using first organization as primary for /${appName}`);
      navigate(`/o/${primaryOrg.id}/${appName}`);
      return;
    }

    // If user has no organizations, redirect to organization selection
    console.log(`🔄 No organizations found, redirecting to organization selection`);
    navigate('/select-organization');
  }, [authLoading, orgLoading, user, currentOrganization, userOrganizations, appName, navigate]);

  // Show loading while determining redirect
  if (authLoading || orgLoading) {
    return <LoadingSpinner />;
  }

  // Fallback redirect if useEffect doesn't handle it
  return <Navigate to="/select-organization" replace />;
};

export default StandaloneAppRedirect;
